import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { http, errMsg } from "@/lib/api";

const Ctx = createContext(null);

export function DataProvider({ children }) {
  const [data, setData] = useState(null);
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [horizon, setHorizon] = useState({ from: 0, to: 9 });
  const [filters, setFilters] = useState(() => {
    try {
      return JSON.parse(sessionStorage.getItem("srm_filters")) || { entity: null, unit: "cr", period: "annual" };
    } catch {
      return { entity: null, unit: "cr", period: "annual" };
    }
  });
  useEffect(() => {
    sessionStorage.setItem("srm_filters", JSON.stringify(filters));
  }, [filters]);
  const [detail, setDetail] = useState(null);
  const [prev, setPrev] = useState(null);
  const resetFilters = useCallback(() => {
    setFilters({ entity: null, unit: "cr", period: "annual" });
    setDetail(null);
    setHorizon({ from: 0, to: 9 });
  }, []);

  useEffect(() => {
    if (!data) return;
    const confirmed = versions.filter((v) => v.status === "confirmed" && v.id !== data.version.id && v.uploaded_at < data.version.uploaded_at);
    const p = confirmed.sort((a, b) => b.uploaded_at.localeCompare(a.uploaded_at))[0];
    if (!p) {
      setPrev(null);
      return;
    }
    http.get(`/versions/${p.id}`).then((r) => setPrev(r.data)).catch(() => setPrev(null));
  }, [data, versions]);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [v, a] = await Promise.all([http.get("/versions"), http.get("/versions/active")]);
      setVersions(v.data);
      setData(a.data);
      const n = a.data.model.years.length;
      setHorizon((h) => ({ from: Math.min(h.from, n - 1), to: Math.min(h.to, n - 1) }));
      setError(null);
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const activate = useCallback(async (id) => {
    await http.post(`/versions/${id}/activate`);
    await refresh();
  }, [refresh]);

  const value = useMemo(
    () => ({ data, versions, loading, error, horizon, setHorizon, refresh, activate, filters, setFilters, detail, setDetail, prev, resetFilters }),
    [data, versions, loading, error, horizon, refresh, activate, filters, detail, prev, resetFilters],
  );
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export const useData = () => useContext(Ctx);
